import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAdminSocket } from '../hooks/useAdminSocket';

function SettingsPage() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();
  const { socket, isConnected } = useAdminSocket('global');

  const [currentLanguage, setCurrentLanguage] = useState<'it' | 'en'>(
    i18n.language as 'it' | 'en'
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!socket || !isConnected) return;

    // Request current language from server
    socket.emit('admin:get-language');

    socket.on('admin:current-language', (data: { language: 'it' | 'en' }) => {
      setCurrentLanguage(data.language);
    });

    socket.on('admin:language-updated', (data: { success: boolean }) => {
      setIsSaving(false);
      if (data.success) {
        setSaveMessage(t('admin:settings.language.changed'));
        setTimeout(() => setSaveMessage(null), 3000);
      }
    });

    return () => {
      socket.off('admin:current-language');
      socket.off('admin:language-updated');
    };
  }, [socket, isConnected, t]);

  const handleLanguageChange = (language: 'it' | 'en') => {
    if (!socket || !isConnected) return;
    setIsSaving(true);
    setCurrentLanguage(language);
    socket.emit('admin:set-language', { language });
  };

  return (
    <div className="min-h-screen bg-gray-900 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="text-blue-400 hover:text-blue-300 mb-4 flex items-center gap-2"
          >
            ← {t('common:actions.back')}
          </button>
          <h1 className="text-4xl font-bold text-white mb-2">
            {t('admin:settings.title')}
          </h1>
          <p className="text-gray-400 text-lg">
            {t('admin:settings.subtitle')}
          </p>
        </div>

        {saveMessage && (
          <div className="mb-6 bg-green-500 bg-opacity-20 border border-green-500 rounded-lg p-4">
            <p className="text-green-300">{saveMessage}</p>
          </div>
        )}

        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-2xl font-bold text-white mb-2">
            {t('admin:settings.language.title')}
          </h2>
          <p className="text-gray-400 mb-6">
            {t('admin:settings.language.description')}
          </p>

          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => handleLanguageChange('it')}
              disabled={isSaving}
              className={`p-6 rounded-lg border-2 transition ${
                currentLanguage === 'it'
                  ? 'border-blue-500 bg-blue-500 bg-opacity-20'
                  : 'border-gray-600 bg-gray-700 hover:border-gray-500'
              } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="text-center">
                <span className="text-4xl mb-2 block">🇮🇹</span>
                <h3 className="text-xl font-bold text-white mb-1">
                  {t('admin:settings.language.italian')}
                </h3>
              </div>
            </button>

            <button
              onClick={() => handleLanguageChange('en')}
              disabled={isSaving}
              className={`p-6 rounded-lg border-2 transition ${
                currentLanguage === 'en'
                  ? 'border-blue-500 bg-blue-500 bg-opacity-20'
                  : 'border-gray-600 bg-gray-700 hover:border-gray-500'
              } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="text-center">
                <span className="text-4xl mb-2 block">🇬🇧</span>
                <h3 className="text-xl font-bold text-white mb-1">
                  {t('admin:settings.language.english')}
                </h3>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
